import { cn } from "@/lib/utils";

type HeroIllustrationProps = {
    className?: string;
};

/**
 * HeroIllustration — original SVG artwork for the TSGo Welcome screen.
 *
 * Composition:
 *  • A rounded green delivery bag as the central object.
 *  • Grocery items peeking out: a leafy carrot, a bread loaf, a milk carton,
 *    an apple, and a bunch of grapes — all constructed from simple shapes
 *    so the SVG stays lightweight (~5KB) and re-colorable via brand tokens.
 *  • A floating "speed" scooter icon that gently drifts using CSS.
 *  • Soft sparkles + delivery-motion accents.
 *
 * Everything is pure SVG — no external assets required.
 */
export function HeroIllustration({ className }: HeroIllustrationProps) {
    return (
        <div
            data-testid="hero-illustration"
            className={cn(
                "relative mx-auto w-full max-w-[520px] aspect-square",
                className,
            )}
            aria-hidden
        >
            {/* Background orbs (behind bag) */}
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute left-6 top-8 h-40 w-40 rounded-full bg-brand/20 blur-3xl" />
                <div className="absolute bottom-6 right-4 h-48 w-48 rounded-full bg-accentbrand/30 blur-3xl" />
            </div>

            {/* Soft dotted circle (delivery radius) */}
            <svg
                className="absolute inset-0 h-full w-full animate-tsgo-spin-slow"
                viewBox="0 0 520 520"
                fill="none"
            >
                <circle
                    cx="260"
                    cy="260"
                    r="220"
                    stroke="url(#ringGradient)"
                    strokeWidth="1.5"
                    strokeDasharray="2 10"
                    opacity="0.35"
                />
                <defs>
                    <linearGradient id="ringGradient" x1="0" y1="0" x2="520" y2="520">
                        <stop offset="0%" stopColor="#00C853" />
                        <stop offset="100%" stopColor="#FFC107" />
                    </linearGradient>
                </defs>
            </svg>

            {/* Grocery bag (centered) */}
            <svg
                viewBox="0 0 400 400"
                className="relative z-10 h-full w-full drop-shadow-[0_20px_50px_rgba(0,200,83,0.25)]"
                fill="none"
            >
                {/* Bag body */}
                <defs>
                    <linearGradient id="bagBody" x1="80" y1="140" x2="320" y2="360" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="#00E676" />
                        <stop offset="50%" stopColor="#00C853" />
                        <stop offset="100%" stopColor="#009F42" />
                    </linearGradient>
                    <linearGradient id="bagShine" x1="120" y1="140" x2="120" y2="360" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="#ffffff" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
                    </linearGradient>
                </defs>

                <path
                    d="M92 160
                       C 92 148, 102 138, 114 138
                       L 286 138
                       C 298 138, 308 148, 308 160
                       L 320 340
                       C 322 356, 310 370, 294 370
                       L 106 370
                       C 90 370, 78 356, 80 340 Z"
                    fill="url(#bagBody)"
                />

                {/* Shine overlay */}
                <path
                    d="M92 160
                       C 92 148, 102 138, 114 138
                       L 200 138
                       L 200 370
                       L 106 370
                       C 90 370, 78 356, 80 340 Z"
                    fill="url(#bagShine)"
                />

                {/* Bag handles */}
                <path
                    d="M138 138 C 138 92, 172 68, 200 68 C 228 68, 262 92, 262 138"
                    stroke="#009F42"
                    strokeWidth="10"
                    strokeLinecap="round"
                />

                {/* TSGo tag */}
                <g transform="translate(228 300)">
                    <rect
                        x="0"
                        y="0"
                        width="70"
                        height="30"
                        rx="8"
                        fill="#ffffff"
                    />
                    <text
                        x="35"
                        y="20"
                        textAnchor="middle"
                        fontFamily="Poppins, sans-serif"
                        fontSize="14"
                        fontWeight="800"
                        fill="#111827"
                    >
                        TSGo
                    </text>
                </g>

                {/* Groceries poking out */}
                {/* Carrot leaves */}
                <g transform="translate(150 60)">
                    <path
                        d="M0 40 Q -6 10 -18 0 Q -4 4 6 20 Q 12 4 20 -4 Q 24 12 14 30 Q 32 4 38 -6 Q 40 22 24 42 Z"
                        fill="#2E7D32"
                    />
                    <path
                        d="M6 40 L 22 100 C 22 108, 8 108, 6 100 Z"
                        fill="#FF7043"
                    />
                    <path
                        d="M10 60 L 18 60 M 10 74 L 18 74"
                        stroke="#D84315"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                    />
                </g>

                {/* Bread */}
                <g transform="translate(216 80)">
                    <path
                        d="M0 24 C -4 6, 40 -6, 64 6 C 82 -2, 94 12, 88 26 C 92 40, 76 52, 46 50 C 20 56, -4 42, 0 24 Z"
                        fill="#F4A83A"
                    />
                    <path
                        d="M12 26 C 10 22, 20 20, 24 24 M 34 20 C 32 16, 44 14, 48 18 M 58 24 C 56 20, 68 20, 72 24"
                        stroke="#B37619"
                        strokeWidth="2"
                        strokeLinecap="round"
                        fill="none"
                    />
                </g>

                {/* Milk carton */}
                <g transform="translate(112 96)">
                    <path
                        d="M0 20 L 24 4 L 48 20 L 48 68 C 48 74, 42 78, 36 78 L 12 78 C 6 78, 0 74, 0 68 Z"
                        fill="#ffffff"
                    />
                    <path d="M0 20 L 24 32 L 48 20" stroke="#E5E7EB" strokeWidth="1.5" fill="none" />
                    <rect x="10" y="42" width="28" height="18" rx="3" fill="#00C853" />
                    <text
                        x="24"
                        y="55"
                        textAnchor="middle"
                        fontFamily="Poppins, sans-serif"
                        fontSize="8"
                        fontWeight="800"
                        fill="#ffffff"
                    >
                        MILK
                    </text>
                </g>

                {/* Apple */}
                <g transform="translate(276 108)">
                    <path
                        d="M20 8 C 8 8, 0 20, 4 34 C 8 46, 20 52, 28 46 C 36 52, 48 46, 52 34 C 56 20, 48 8, 36 8 C 32 8, 30 10, 28 12 C 26 10, 24 8, 20 8 Z"
                        fill="#EF5350"
                    />
                    <path d="M28 12 C 28 6, 32 2, 36 2" stroke="#5D4037" strokeWidth="2" fill="none" strokeLinecap="round" />
                    <ellipse cx="34" cy="4" rx="4" ry="2" fill="#2E7D32" transform="rotate(-20 34 4)" />
                    <ellipse cx="16" cy="20" rx="4" ry="6" fill="#ffffff" opacity="0.35" />
                </g>

                {/* Grapes */}
                <g transform="translate(180 108)">
                    <circle cx="10" cy="6" r="6" fill="#7E57C2" />
                    <circle cx="22" cy="6" r="6" fill="#7E57C2" />
                    <circle cx="16" cy="16" r="6" fill="#8E63CE" />
                    <circle cx="28" cy="16" r="6" fill="#7E57C2" />
                    <circle cx="22" cy="26" r="6" fill="#8E63CE" />
                    <path d="M16 0 C 14 -6, 22 -8, 24 -2" stroke="#2E7D32" strokeWidth="2" fill="none" strokeLinecap="round" />
                </g>
            </svg>

            {/* Floating scooter chip (top-right) */}
            <div className="absolute -right-2 top-6 z-20 animate-tsgo-float sm:right-2 md:-right-4 md:top-10">
                <div className="flex items-center gap-2 rounded-full bg-white px-3 py-2 shadow-elevated ring-1 ring-black/5">
                    <span className="grid h-8 w-8 place-items-center rounded-full bg-brand text-white">
                        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2">
                            <path d="M4 17h2a2 2 0 1 0 4 0h4a2 2 0 1 0 4 0h2v-4l-3-4h-4V7a2 2 0 0 0-2-2H5" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="7" cy="17" r="2"/>
                            <circle cx="17" cy="17" r="2"/>
                        </svg>
                    </span>
                    <div className="leading-tight">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                            Arriving in
                        </p>
                        <p className="text-sm font-extrabold text-foreground">
                            12 min
                        </p>
                    </div>
                </div>
            </div>

            {/* Floating discount chip (bottom-left) */}
            <div className="absolute -left-2 bottom-8 z-20 animate-tsgo-float-slow sm:-left-4">
                <div className="flex items-center gap-2 rounded-full bg-accentbrand px-3 py-2 shadow-elevated ring-1 ring-black/5">
                    <span className="grid h-8 w-8 place-items-center rounded-full bg-white text-accentbrand-700">
                        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.4">
                            <path d="M5 12l4 4 10-10" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </span>
                    <div className="leading-tight">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-black/70">
                            Save up to
                        </p>
                        <p className="text-sm font-extrabold text-black">
                            40% off
                        </p>
                    </div>
                </div>
            </div>

            {/* Sparkles */}
            <div className="pointer-events-none absolute inset-0 z-10">
                <span className="absolute left-8 top-16 h-2 w-2 rotate-45 rounded-sm bg-accentbrand animate-pulse-brand" />
                <span className="absolute right-12 top-24 h-1.5 w-1.5 rounded-full bg-brand animate-pulse-brand [animation-delay:0.4s]" />
                <span className="absolute right-20 bottom-24 h-2 w-2 rotate-45 rounded-sm bg-brand animate-pulse-brand [animation-delay:0.8s]" />
            </div>
        </div>
    );
}

export default HeroIllustration;
